import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { MutableRefObject, ReactNode } from 'react';
import { getAuth, onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import type { Auth, User as FirebaseUser } from 'firebase/auth';
import type { FirebaseApp } from 'firebase/app';
import axios from 'axios';
import type { TokenExchangeResponse } from '@klareospace/pharos-lib-types';
import { decodeJwtPayload } from '@klareospace/pharos-lib-utils';
import { SESSION_TIMEOUT_MS } from '../constants';
import { exchangeFirebaseToken, refreshPharosToken } from './token-exchange';
import type { TokenExchangeConfig } from './token-exchange';
import { useImpersonationSession } from './use-impersonation-session';
import { SESSION_ENDED_REASON } from './session-ended-reason';
import type { SessionEndedReason } from './session-ended-reason';
import { SIGNED_OUT_STATE } from './signed-out-state';

export { SESSION_ENDED_REASON } from './session-ended-reason';
export type { SessionEndedReason } from './session-ended-reason';

// --- Constants ---

const PROACTIVE_REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

function isTokenExpiringSoon(expiryMs: number | null): boolean {
  if (!expiryMs) return false;
  return expiryMs - Date.now() < PROACTIVE_REFRESH_THRESHOLD_MS;
}

/** Anchors expiry to the JWT's own `exp` claim, capped at `now + sessionTimeoutMs`; falls back to the cap when `exp` is missing or not a finite number. */
function computeTokenExpiryMs(token: string, sessionTimeoutMs: number): number {
  const fallbackExpiryMs = Date.now() + sessionTimeoutMs;
  const exp = decodeJwtPayload(token)?.exp;
  if (typeof exp !== 'number' || !Number.isFinite(exp)) return fallbackExpiryMs;
  return Math.min(exp * 1000, fallbackExpiryMs);
}

/**
 * Returns true when an exchange failure should be treated as an offline condition
 * (keep the session alive) rather than a genuine server rejection (sign out).
 * Three offline signals are recognized:
 *   1. axios no-response error (ERR_NETWORK / timeout)
 *   2. Firebase getIdToken failure while offline (auth/network-request-failed)
 *   3. The injected connectivity check explicitly reports offline
 */
function isOfflineExchangeFailure(err: unknown, isOnline: (() => boolean) | undefined): boolean {
  if (axios.isAxiosError(err) && !err.response) return true;
  if ((err as { code?: string })?.code === 'auth/network-request-failed') return true;
  if (isOnline?.() === false) return true;
  return false;
}

/**
 * Signs out of Firebase and, when there was no Firebase user to begin with, immediately resets
 * signOutInProgressRef — firebaseSignOut() emits no onAuthStateChanged event in that case (null to
 * null is not a change), so nothing else would ever reset the guard.
 */
async function finishFirebaseSignOut(
  auth: Auth,
  hadNoFirebaseUser: boolean,
  signOutInProgressRef: MutableRefObject<boolean>,
): Promise<void> {
  await firebaseSignOut(auth);
  if (hadNoFirebaseUser) signOutInProgressRef.current = false;
}

// --- Types ---

export interface PharosAuthUser {
  email: string;
  name: string;
}

export interface SignInStrategies {
  /** Change the current user's password (re-authenticates then updates) */
  changePassword?: (currentPassword: string, newPassword: string) => Promise<void>;
  /** Email/password sign-in */
  email?: (email: string, password: string) => Promise<void>;
  /** Google SSO popup */
  google?: () => Promise<void>;
  /** Microsoft SSO popup */
  microsoft?: () => Promise<void>;
  /** Send password reset email */
  resetPassword?: (email: string) => Promise<void>;
}

export interface PharosAuthState {
  /** Auth error message (for display on login page) */
  error: string | null;
  /** Whether the user is authenticated (has valid Klareo token) */
  isAuthenticated: boolean;
  /** Whether the current session is an impersonation session */
  isImpersonating: boolean;
  /** Whether auth state is still being determined */
  isLoading: boolean;
  /** The Klareo JWT token (null if not authenticated) */
  klareoToken: string | null;
  /** Reason the session status should be shown (null if there is no session status message to show) */
  sessionEndedReason: SessionEndedReason | null;
  /** Basic user info from Firebase */
  user: PharosAuthUser | null;
}

export interface PharosAuthContextValue extends PharosAuthState {
  /** Change the current user's password (only available if changePassword strategy was provided) */
  changePassword?: (currentPassword: string, newPassword: string) => Promise<void>;
  /** Get the current Klareo token (for use as TokenProvider) */
  getToken: () => Promise<string | null>;
  /** Sign out with the session-expired reason. Wire this (not signOut) to the API client's onUnauthorized callback. */
  handleUnauthorized: () => void;
  /** Notify the auth provider that user activity occurred (e.g., an API request was sent).
   *  Resets the inactivity timer. Only meaningful when inactivityTimeoutMs is configured. */
  notifyActivity: () => void;
  /** Pause token exchange (used during activation flow) */
  pauseExchange: () => void;
  /** Resume token exchange */
  resumeExchange: () => void;
  /** Send password reset email (only available if resetPassword strategy was provided) */
  sendPasswordResetEmail?: (email: string) => Promise<void>;
  /** Sign in with email/password (only available if email strategy was provided) */
  signInWithEmail?: (email: string, password: string) => Promise<void>;
  /** Sign in with Google SSO (only available if google strategy was provided) */
  signInWithGoogle?: () => Promise<void>;
  /** Sign in with Microsoft SSO (only available if microsoft strategy was provided) */
  signInWithMicrosoft?: () => Promise<void>;
  /** Sign out and clear tokens */
  signOut: () => Promise<void>;
}

export interface PharosAuthProviderProps {
  children: ReactNode;
  /** Callback to clear the persisted refresh token (on sign-out).
   *  Required when enableRefresh is true. */
  clearPersistedRefreshToken?: () => Promise<void>;
  /** Enable token refresh. When true, the provider will attempt to refresh
   *  expired tokens using the refresh token before signing out.
   *  Default: false (web behavior — sign out on expiry). */
  enableRefresh?: boolean;
  /** Config for token exchange. Set `exchangeConfig.client` to mint a `client` claim into the
   *  session token (e.g. mobile passes `KLAREO_CLIENT.MOBILE_DRIVER`); web omits it. */
  exchangeConfig: TokenExchangeConfig;
  /** Firebase app instance (from initializeFirebase) */
  firebaseApp: FirebaseApp;
  /** Callback to read the persisted refresh token.
   *  Required when enableRefresh is true. */
  getPersistedRefreshToken?: () => Promise<string | null>;
  /** Inactivity timeout in milliseconds. If set, auto-signs out after this period of no backend API requests. Callers must invoke notifyActivity() to reset the timer. */
  inactivityTimeoutMs?: number;
  /** Optional connectivity check. When it returns false, an exchange failure is treated
   *  as an offline condition (keep-alive) rather than a server rejection (sign-out).
   *  Mobile passes () => onlineManager.isOnline(); web omits it. */
  isOnline?: () => boolean;
  /** Callback to persist the refresh token (e.g., expo-secure-store on mobile).
   *  Required when enableRefresh is true. */
  onRefreshTokenReceived?: (refreshToken: string) => Promise<void>;
  /** Optional callback invoked immediately before Firebase sign-out. Use this to clear any
   *  app-level caches (e.g., React Query cache) so the next user does not see stale data. */
  onSignOut?: () => void;
  /** Optional synchronous function to read an impersonation token (e.g., from URL params).
   *  Called once on initial render. Return null if no impersonation token is present. */
  readImpersonationToken?: () => string | null;
  /** Session timeout in milliseconds (default: 12 * 60 * 60 * 1000 = 12 hours) */
  sessionTimeoutMs?: number;
  /** Sign-in strategies (app-specific). Each injected strategy gets wrapped with centralized loading/error handling. */
  signInStrategies?: SignInStrategies;
}

// --- Context ---

const PharosAuthContext = createContext<PharosAuthContextValue | null>(null);

// --- Provider ---

export function PharosAuthProvider({
  children,
  clearPersistedRefreshToken,
  enableRefresh = false,
  exchangeConfig,
  firebaseApp,
  getPersistedRefreshToken,
  inactivityTimeoutMs,
  isOnline,
  onRefreshTokenReceived,
  onSignOut,
  readImpersonationToken,
  sessionTimeoutMs = SESSION_TIMEOUT_MS,
  signInStrategies,
}: PharosAuthProviderProps): React.JSX.Element {
  const attemptTokenRefreshRef = useRef<(() => Promise<boolean>) | null>(null);
  const exchangePausedRef = useRef(false);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const klareoTokenRef = useRef<string | null>(null);
  const refreshPromiseRef = useRef<Promise<boolean> | null>(null);
  const refreshTokenRef = useRef<string | null>(null);
  const sessionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks the reason the session ended, so onAuthStateChanged can preserve it in state.
  const sessionEndedReasonRef = useRef<SessionEndedReason | null>(null);
  // Tracks whether a sign-out was initiated by a token exchange error, so that the
  // onAuthStateChanged callback does not overwrite the error state with null.
  const signOutDueToErrorRef = useRef(false);
  // Guards signOut() against re-entry until onAuthStateChanged confirms it.
  const signOutInProgressRef = useRef(false);
  const tokenExpiryRef = useRef<number | null>(null);

  // A stable ref to the provider's setState, passed into useImpersonationSession so that
  // the impersonation expiry timer can update auth state. Populated after useState below.
  const setStateRef = useRef<React.Dispatch<React.SetStateAction<PharosAuthState>>>(
    // Placeholder — always replaced before any effect fires (React renders synchronously)
    () => undefined,
  );

  const impersonation = useImpersonationSession(
    readImpersonationToken,
    { inactivityTimerRef, klareoTokenRef, sessionTimerRef, tokenExpiryRef },
    setStateRef,
  );

  // Lazy initializer: when an impersonation token is provided the state is
  // immediately authenticated so TanStack Router's beforeLoad guard does not
  // redirect to /login on the first render.
  const [state, setState] = useState<PharosAuthState>(() => impersonation.initialState);

  // Keep the setter ref up-to-date every render so useImpersonationSession's timer
  // callback always calls the current setState.
  setStateRef.current = setState;

  const auth = useMemo(() => getAuth(firebaseApp), [firebaseApp]);

  // Clear session timer on unmount
  useEffect(() => {
    return (): void => {
      if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    };
  }, []);

  /**
   * Handles an exchange network failure (ERR_NETWORK / timeout — no HTTP response).
   * If `getPersistedRefreshToken` is provided AND a refresh token is stored, the
   * session is kept alive in an offline-tolerant state (isAuthenticated=true, klareoToken=null).
   * Returns true if the offline path was taken (caller must NOT sign out), false otherwise.
   */
  const handleExchangeNetworkFailure = useCallback(
    async (firebaseUser: FirebaseUser): Promise<boolean> => {
      if (!getPersistedRefreshToken) return false;
      const persistedToken = await getPersistedRefreshToken().catch(() => null);
      if (persistedToken === null) return false;

      setState({
        error: null,
        isAuthenticated: true,
        isImpersonating: false,
        isLoading: false,
        klareoToken: null,
        sessionEndedReason: null,
        user: {
          email: firebaseUser.email ?? '',
          name: firebaseUser.displayName ?? firebaseUser.email ?? '',
        },
      });
      return true; // offline-tolerant path taken — do NOT sign out Firebase
    },
    [getPersistedRefreshToken],
  );

  /**
   * Reset the session timer after a successful token refresh or re-exchange.
   * Schedules the next refresh attempt (if enableRefresh) or a sign-out at the end of the session.
   * `durationMs` must be the remaining time to the token's real expiry, not the raw sessionTimeoutMs.
   */
  const resetSessionTimer = useCallback(
    (durationMs: number): void => {
      if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
      sessionTimerRef.current = setTimeout(() => {
        if (enableRefresh) {
          void attemptTokenRefreshRef.current?.();
        } else {
          sessionEndedReasonRef.current = SESSION_ENDED_REASON.MAX_DURATION;
          void firebaseSignOut(auth);
        }
      }, durationMs);
    },
    [auth, enableRefresh],
  );

  /**
   * Commits a successful exchange result to refs and state, and arms the session timer.
   * Extracted to keep performTokenExchange below the cyclomatic-complexity limit.
   */
  const applyExchangeSuccess = useCallback(
    async (firebaseUser: FirebaseUser, result: TokenExchangeResponse): Promise<void> => {
      klareoTokenRef.current = result.token;
      refreshTokenRef.current = result.refreshToken ?? null;
      tokenExpiryRef.current = computeTokenExpiryMs(result.token, sessionTimeoutMs);

      if (result.refreshToken && onRefreshTokenReceived) {
        await onRefreshTokenReceived(result.refreshToken);
      }

      resetSessionTimer(tokenExpiryRef.current - Date.now());

      setState({
        error: null,
        isAuthenticated: true,
        isImpersonating: false,
        isLoading: false,
        klareoToken: result.token,
        sessionEndedReason: null,
        user: {
          email: firebaseUser.email ?? '',
          name: firebaseUser.displayName ?? firebaseUser.email ?? '',
        },
      });
    },
    [onRefreshTokenReceived, resetSessionTimer, sessionTimeoutMs],
  );

  /**
   * Handles a failed token exchange. Classifies the error as an offline condition or a
   * genuine server rejection and takes the appropriate action (keep-alive or sign-out).
   * Extracted to keep performTokenExchange below the cyclomatic-complexity limit.
   * Returns true if the offline-tolerant path was taken (caller should NOT sign out).
   */
  const handleExchangeError = useCallback(
    async (err: unknown, firebaseUser: FirebaseUser): Promise<boolean> => {
      if (isOfflineExchangeFailure(err, isOnline)) {
        const offlineTaken = await handleExchangeNetworkFailure(firebaseUser);
        if (offlineTaken) return true;
      }

      const message = err instanceof Error ? err.message : 'Token exchange failed';
      klareoTokenRef.current = null;
      refreshTokenRef.current = null;
      tokenExpiryRef.current = null;
      setState({ ...SIGNED_OUT_STATE, error: message, sessionEndedReason: null });
      signOutDueToErrorRef.current = true;
      await firebaseSignOut(auth);
      return false;
    },
    [auth, handleExchangeNetworkFailure, isOnline],
  );

  // Exchange Firebase token for Klareo JWT
  const performTokenExchange = useCallback(
    async (firebaseUser: FirebaseUser): Promise<void> => {
      try {
        const idToken = await firebaseUser.getIdToken(/* forceRefresh */ true);
        const result = await exchangeFirebaseToken(idToken, exchangeConfig);
        await applyExchangeSuccess(firebaseUser, result);
      } catch (err: unknown) {
        await handleExchangeError(err, firebaseUser);
      }
    },
    [applyExchangeSuccess, exchangeConfig, handleExchangeError],
  );

  // Listen to Firebase auth state changes (skip when impersonating)
  useEffect(() => {
    if (impersonation.token) {
      // Impersonation mode: skip Firebase listener, mark loading as done
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        // A real sign-in always means any stale sign-out guard/reason must be cleared,
        // even when the activation flow is about to pause the exchange below.
        signOutInProgressRef.current = false;
        sessionEndedReasonRef.current = null;
        if (exchangePausedRef.current) {
          // Activation flow — skip exchange, the hook will sign out shortly
          return;
        }
        void performTokenExchange(firebaseUser);
      } else {
        // User signed out
        klareoTokenRef.current = null;
        refreshTokenRef.current = null;
        tokenExpiryRef.current = null;
        signOutInProgressRef.current = false;
        if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
        if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
        if (signOutDueToErrorRef.current) {
          // Error-initiated sign-out — preserve the error state already set by the catch block
          signOutDueToErrorRef.current = false;
        } else if (sessionEndedReasonRef.current) {
          // Timeout-initiated sign-out (inactivity or maxDuration) — surface the expiry reason
          const reason = sessionEndedReasonRef.current;
          sessionEndedReasonRef.current = null;
          setState({ ...SIGNED_OUT_STATE, error: null, sessionEndedReason: reason });
        } else {
          // Normal sign-out — clear everything including error and expiry reason
          setState({ ...SIGNED_OUT_STATE, error: null, sessionEndedReason: null });
        }
      }
    });
    return unsubscribe;
  }, [auth, impersonation.token, performTokenExchange]);

  const notifyActivity = useCallback((): void => {
    if (!inactivityTimeoutMs || !state.isAuthenticated) return;
    // Impersonation sessions have a hard expiry (JWT exp) — no inactivity timeout
    if (impersonation.token) return;
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(() => {
      sessionEndedReasonRef.current = SESSION_ENDED_REASON.INACTIVITY;
      void firebaseSignOut(auth);
    }, inactivityTimeoutMs);
  }, [auth, impersonation.token, inactivityTimeoutMs, state.isAuthenticated]);

  const pauseExchange = useCallback((): void => {
    exchangePausedRef.current = true;
  }, []);

  const resumeExchange = useCallback((): void => {
    exchangePausedRef.current = false;
  }, []);

  // Start the initial inactivity timer when the user becomes authenticated
  useEffect(() => {
    if (!inactivityTimeoutMs || !state.isAuthenticated) return;
    // Impersonation sessions have a hard expiry (JWT exp) — no inactivity timeout
    if (impersonation.token) return;

    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(() => {
      sessionEndedReasonRef.current = SESSION_ENDED_REASON.INACTIVITY;
      void firebaseSignOut(auth);
    }, inactivityTimeoutMs);

    return (): void => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    };
  }, [auth, impersonation.token, inactivityTimeoutMs, state.isAuthenticated]);

  const signInWithEmail = useMemo(() => {
    const emailStrategy = signInStrategies?.email;
    if (!emailStrategy) return undefined;
    return async (email: string, password: string): Promise<void> => {
      setState((prev) => ({ ...prev, error: null, isLoading: true, sessionEndedReason: null }));
      try {
        await emailStrategy(email, password);
        // onAuthStateChanged in the useEffect will handle token exchange
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Sign-in failed';
        setState((prev) => ({ ...prev, error: message, isLoading: false }));
        throw err;
      }
    };
  }, [signInStrategies]);

  const signInWithGoogle = useMemo(() => {
    const googleStrategy = signInStrategies?.google;
    if (!googleStrategy) return undefined;
    return async (): Promise<void> => {
      setState((prev) => ({ ...prev, error: null, isLoading: true, sessionEndedReason: null }));
      try {
        await googleStrategy();
        // onAuthStateChanged in the useEffect will handle token exchange
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Sign-in failed';
        setState((prev) => ({ ...prev, error: message, isLoading: false }));
        throw err;
      }
    };
  }, [signInStrategies]);

  const signInWithMicrosoft = useMemo(() => {
    const microsoftStrategy = signInStrategies?.microsoft;
    if (!microsoftStrategy) return undefined;
    return async (): Promise<void> => {
      setState((prev) => ({ ...prev, error: null, isLoading: true, sessionEndedReason: null }));
      try {
        await microsoftStrategy();
        // onAuthStateChanged in the useEffect will handle token exchange
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Sign-in failed';
        setState((prev) => ({ ...prev, error: message, isLoading: false }));
        throw err;
      }
    };
  }, [signInStrategies]);

  const sendPasswordResetEmail = useMemo(() => {
    const resetPasswordStrategy = signInStrategies?.resetPassword;
    if (!resetPasswordStrategy) return undefined;
    return async (email: string): Promise<void> => {
      await resetPasswordStrategy(email);
    };
  }, [signInStrategies]);

  const changePassword = useMemo(() => {
    const changePasswordStrategy = signInStrategies?.changePassword;
    if (!changePasswordStrategy) return undefined;
    return async (currentPassword: string, newPassword: string): Promise<void> => {
      await changePasswordStrategy(currentPassword, newPassword);
    };
  }, [signInStrategies]);

  const signOut = useCallback(async (): Promise<void> => {
    if (signOutInProgressRef.current) return;
    signOutInProgressRef.current = true;
    // No Firebase user means firebaseSignOut() below will emit no onAuthStateChanged event
    // (null to null is not a change), so nothing will reset the guard — self-heal here instead.
    const hadNoFirebaseUser = auth.currentUser === null;

    klareoTokenRef.current = null;
    refreshTokenRef.current = null;
    tokenExpiryRef.current = null;
    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);

    // Preserve the logout reason before any auth-state transition so onAuthStateChanged
    // cannot overwrite the intended user-initiated sign-out message.
    sessionEndedReasonRef.current ??= SESSION_ENDED_REASON.USER_LOGOUT;

    // Clear the persisted refresh token from secure storage (mobile)
    if (clearPersistedRefreshToken) {
      try {
        await clearPersistedRefreshToken();
      } catch (error: unknown) {
        console.error('PharosAuthProvider: clearPersistedRefreshToken failed during sign-out.', error);
      }
    }

    // Notify the consumer (e.g., to clear React Query cache) before Firebase sign-out
    // so all user-specific cached data is flushed before the next user can sign in.
    try {
      onSignOut?.();
    } catch (error: unknown) {
      console.error('PharosAuthProvider: onSignOut callback failed during sign-out.', error);
    }

    if (!impersonation.token) {
      await finishFirebaseSignOut(auth, hadNoFirebaseUser, signOutInProgressRef);
      return;
    }

    // Impersonation session: also sign out Firebase to prevent session restoration
    await finishFirebaseSignOut(auth, hadNoFirebaseUser, signOutInProgressRef);
    impersonation.clearImpersonation();
    setState({ ...SIGNED_OUT_STATE, error: null, sessionEndedReason: SESSION_ENDED_REASON.USER_LOGOUT });
  }, [auth, clearPersistedRefreshToken, impersonation, onSignOut]);

  // Wire to the API client's onUnauthorized — unlike signOut(), always labels the reason MAX_DURATION.
  const handleUnauthorized = useCallback((): void => {
    // No session to end — e.g. a 401 on the login page, or an in-flight refetch right after a
    // sign-out. Guard on the Firebase user, not klareoTokenRef: the offline keep-alive path
    // (isAuthenticated=true, klareoToken=null, Firebase user present) must still sign out.
    if (auth.currentUser === null && !impersonation.token) return;
    sessionEndedReasonRef.current = SESSION_ENDED_REASON.MAX_DURATION;
    void signOut();
  }, [auth, impersonation.token, signOut]);

  /**
   * Core refresh logic — calls POST /refresh, updates token refs, persists the new refresh token,
   * resets the session timer, and updates React state.
   * Returns true on success, false on any failure.
   */
  const executeTokenRefresh = useCallback(async (): Promise<boolean> => {
    try {
      const expiredToken = klareoTokenRef.current;
      const storedRefreshToken = refreshTokenRef.current ?? (await getPersistedRefreshToken?.()) ?? null;

      if (!expiredToken || !storedRefreshToken) return false;

      const result = await refreshPharosToken(expiredToken, storedRefreshToken, exchangeConfig);

      klareoTokenRef.current = result.token;
      refreshTokenRef.current = result.refreshToken ?? null;
      tokenExpiryRef.current = computeTokenExpiryMs(result.token, sessionTimeoutMs);

      if (result.refreshToken && onRefreshTokenReceived) {
        await onRefreshTokenReceived(result.refreshToken);
      }

      resetSessionTimer(tokenExpiryRef.current - Date.now());
      setState((prev) => ({ ...prev, klareoToken: result.token }));
      return true;
    } catch {
      return false;
    }
  }, [exchangeConfig, getPersistedRefreshToken, onRefreshTokenReceived, resetSessionTimer, sessionTimeoutMs]);

  /**
   * Attempt to silently refresh the Pharos JWT using the stored stateless refresh token.
   * Deduplicates concurrent calls: if a refresh is already in progress, returns the same promise.
   * Updates klareoTokenRef, refreshTokenRef, tokenExpiryRef, and persists the new refresh token.
   * Returns true on success, false on failure.
   */
  const attemptTokenRefresh = useCallback((): Promise<boolean> => {
    if (refreshPromiseRef.current) return refreshPromiseRef.current;

    const promise = executeTokenRefresh().finally(() => {
      refreshPromiseRef.current = null;
    });

    refreshPromiseRef.current = promise;
    return promise;
  }, [executeTokenRefresh]);

  // Keep the ref in sync with the latest stable callback so resetSessionTimer can call it
  // without creating a circular useCallback dependency.
  attemptTokenRefreshRef.current = attemptTokenRefresh;

  /**
   * Fall back to Firebase re-exchange when refresh token is unavailable or exhausted.
   * Forces a new Firebase ID token and re-runs the full /exchange flow.
   * Returns true on success, false on failure.
   */
  const attemptFirebaseReExchange = useCallback(async (): Promise<boolean> => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return false;

      const idToken = await currentUser.getIdToken(/* forceRefresh */ true);
      const result = await exchangeFirebaseToken(idToken, exchangeConfig);

      klareoTokenRef.current = result.token;
      refreshTokenRef.current = result.refreshToken ?? null;
      tokenExpiryRef.current = computeTokenExpiryMs(result.token, sessionTimeoutMs);

      if (result.refreshToken && onRefreshTokenReceived) {
        await onRefreshTokenReceived(result.refreshToken);
      }

      resetSessionTimer(tokenExpiryRef.current - Date.now());

      setState((prev) => ({ ...prev, klareoToken: result.token }));
      return true;
    } catch {
      return false;
    }
  }, [auth, exchangeConfig, onRefreshTokenReceived, resetSessionTimer, sessionTimeoutMs]);

  /**
   * Attempts a full Firebase → Klareo token re-exchange when the session is in the
   * offline-tolerant state (klareoToken=null, no tokenExpiry, Firebase user present).
   * Returns the new token on success, or null if still offline (caller must NOT sign out).
   */
  const recoverOfflineToken = useCallback(async (): Promise<string | null> => {
    if (!auth.currentUser) return null;
    try {
      const idToken = await auth.currentUser.getIdToken(/* forceRefresh */ true);
      const result = await exchangeFirebaseToken(idToken, exchangeConfig);

      klareoTokenRef.current = result.token;
      refreshTokenRef.current = result.refreshToken ?? null;
      tokenExpiryRef.current = computeTokenExpiryMs(result.token, sessionTimeoutMs);

      if (result.refreshToken && onRefreshTokenReceived) {
        await onRefreshTokenReceived(result.refreshToken);
      }

      resetSessionTimer(tokenExpiryRef.current - Date.now());
      setState((prev) => ({ ...prev, error: null, klareoToken: result.token }));
      return klareoTokenRef.current;
    } catch {
      // Still offline (or another network failure) — return null gracefully, no sign-out
      return null;
    }
  }, [auth, exchangeConfig, onRefreshTokenReceived, resetSessionTimer, sessionTimeoutMs]);

  /**
   * Called by getToken when the session token has expired. When offline, returns null immediately
   * (keep session, retry on reconnect) without attempting any network operations that are
   * guaranteed to fail. When online, attempts a silent refresh (when enableRefresh is true), then
   * falls back to Firebase re-exchange; if both fail, signs out.
   * Extracted from getToken to keep both functions below the cyclomatic-complexity limit.
   */
  const handleExpiredToken = useCallback(async (): Promise<string | null> => {
    // Offline: skip refresh and re-exchange (both will fail) — keep session, retry on reconnect
    if (isOnline?.() === false) {
      return null;
    }

    if (enableRefresh) {
      // Attempt silent refresh via stateless refresh token
      const refreshed = await attemptTokenRefresh();
      if (refreshed) return klareoTokenRef.current;

      // Refresh failed — fall back to Firebase re-exchange
      const reExchanged = await attemptFirebaseReExchange();
      if (reExchanged) return klareoTokenRef.current;
    }

    // No refresh enabled, or all attempts failed on an online connection — sign out.
    sessionEndedReasonRef.current = SESSION_ENDED_REASON.MAX_DURATION;
    await signOut();
    return null;
  }, [attemptFirebaseReExchange, attemptTokenRefresh, enableRefresh, isOnline, signOut]);

  const getToken = useCallback((): Promise<string | null> => {
    // Offline-tolerant recovery: klareoToken is null (exchange failed while offline) but
    // the session is still considered authenticated (isAuthenticated=true, Firebase user present).
    // Attempt a full re-exchange now — recoverOfflineToken checks auth.currentUser internally
    // and returns null gracefully if still offline (no sign-out). The next call retries.
    if (klareoTokenRef.current === null && !tokenExpiryRef.current) {
      return recoverOfflineToken();
    }

    // Check if session has expired — delegate to handleExpiredToken for refresh / offline guard
    if (tokenExpiryRef.current && Date.now() >= tokenExpiryRef.current) {
      return handleExpiredToken();
    }

    // Proactive refresh: if token expires within 5 minutes, refresh in background (fire and forget)
    if (enableRefresh && isTokenExpiringSoon(tokenExpiryRef.current)) {
      void attemptTokenRefresh();
    }

    return Promise.resolve(klareoTokenRef.current);
  }, [attemptTokenRefresh, enableRefresh, handleExpiredToken, recoverOfflineToken]);

  const value = useMemo<PharosAuthContextValue>(
    () => ({
      ...state,
      changePassword,
      getToken,
      handleUnauthorized,
      notifyActivity,
      pauseExchange,
      resumeExchange,
      sendPasswordResetEmail,
      signInWithEmail,
      signInWithGoogle,
      signInWithMicrosoft,
      signOut,
    }),
    [
      state,
      changePassword,
      getToken,
      handleUnauthorized,
      notifyActivity,
      pauseExchange,
      resumeExchange,
      sendPasswordResetEmail,
      signInWithEmail,
      signInWithGoogle,
      signInWithMicrosoft,
      signOut,
    ],
  );

  return <PharosAuthContext.Provider value={value}>{children}</PharosAuthContext.Provider>;
}

// --- Hook ---

export function usePharosAuth(): PharosAuthContextValue {
  const context = useContext(PharosAuthContext);
  if (!context) {
    throw new Error('usePharosAuth must be used within a <PharosAuthProvider>');
  }
  return context;
}
